import { ReactNode, useMemo } from 'react'
import { DateTime } from 'luxon'

import { getStripeCustomerURL } from './AccountDetailsTabPanel'
import { Badge, Card, Link, Text, List } from '@/ui'
import { getIsFetching } from '@/reducers/fetch'
import {
  adminIsUserInFreeTrial,
  getUserById,
  selectPrimaryMembershipForUser,
} from '@/selectors/user.selectors'
import {
  ADMIN_REQUEST_SUBSCRIPTIONS_BY_USER_ID,
  type LiteDowngradeEligibilityResponse,
} from '@/actions/adminActions'
import { StripeSubscription } from '@/reducers/subscription.slice'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { TypographyStyles } from '@/styles'

const isProd = process.env.VITE_IS_PROD === 'true'

const STRIPE_DASHBOARD_BASE_URL = 'https://dashboard.stripe.com'
export const getStripeSubscriptionsUrl = (subscriptionId: string) =>
  `${STRIPE_DASHBOARD_BASE_URL}/${
    !isProd ? 'test/' : ''
  }subscriptions/${subscriptionId}`

const GreenText = ({ children }: { children?: ReactNode }) => (
  <span className="text-green-500">{children}</span>
)

interface Props {
  userId: number
  isUnservicedFreeTrial?: boolean
  subscriptions: StripeSubscription[] | undefined
  liteDowngradeEligibility: LiteDowngradeEligibilityResponse
}

const LiteDowngradeEligibilityRow = ({
  value,
}: {
  value: LiteDowngradeEligibilityResponse
}) => {
  if (value === null) {
    return (
      <Text variant={TypographyStyles.H3}>
        Lite Downgrade Eligibility:{' '}
        <span className="text-neutral-500">Unknown</span>
      </Text>
    )
  }
  if (value.eligible) {
    return (
      <Text variant={TypographyStyles.H3}>
        Lite Downgrade Eligibility:{' '}
        <Badge variant="secondary" color="green" size="sm">
          Yes
        </Badge>
      </Text>
    )
  }
  return (
    <Text variant={TypographyStyles.H3}>
      Lite Downgrade Eligibility:{' '}
      <Badge variant="secondary" color="red" size="sm">
        No
        {value.blockingReasons.length > 0 &&
          ` — ${value.blockingReasons.join(', ')}`}
      </Badge>
    </Text>
  )
}

export const StripeSubscriptionDetailsPanel = ({
  userId,
  isUnservicedFreeTrial,
  subscriptions,
  liteDowngradeEligibility,
}: Props) => {
  const subscriptionsLoading = useReselector(
    getIsFetching,
    ADMIN_REQUEST_SUBSCRIPTIONS_BY_USER_ID
  )
  const { createdAt, stripeCustomer } = useReselector(getUserById, userId) ?? {}
  const primaryMembership = useReselector(
    selectPrimaryMembershipForUser,
    userId
  )
  const isInFreeTrial = useReselector(adminIsUserInFreeTrial, userId)

  const earliestSubscriptionStartDate = useMemo(() => {
    const startDate = (subscriptions || []).sort(
      (sub1, sub2) => sub1.start_date - sub2.start_date
    )[0]?.start_date
    if (startDate) {
      return DateTime.fromSeconds(startDate).toFormat(
        DATE_FORMATS_LUXON.DISPLAY_SHORT
      )
    }
    return ''
  }, [subscriptions])

  return (
    <Card className="w-full">
      <Text variant={TypographyStyles.H1}>Stripe Subscription Details</Text>
      {isInFreeTrial && (
        <>
          <br />
          <Text variant={TypographyStyles.H3}>
            {isUnservicedFreeTrial
              ? 'User is in Free Trial (14 days free with no BK service)'
              : 'User is in Free Trial'}
          </Text>
          <Text variant={TypographyStyles.BODY_LG}>
            <p>
              <b>Free Trial Promo Code: </b>
              {primaryMembership?.freeTrialPromoCode}
            </p>
          </Text>
          <Text variant={TypographyStyles.BODY_LG}>
            <p>
              <b>Free Trial End Date: </b>
              {primaryMembership?.trialEndDate}
            </p>
          </Text>
        </>
      )}
      {stripeCustomer && (
        <>
          <br />
          <Text variant={TypographyStyles.H3}>
            Stripe Customer:{' '}
            <Link href={getStripeCustomerURL(stripeCustomer)} newPage>
              {stripeCustomer}
            </Link>
          </Text>
        </>
      )}
      {subscriptionsLoading && !subscriptions ? (
        <>
          <br />
          <Text variant={TypographyStyles.H3}>
            Loading subscription details...
          </Text>
        </>
      ) : (
        <>
          {subscriptions && (
            <>
              <br />
              <Text variant={TypographyStyles.H3}>Stripe Subscriptions:</Text>
              <List bulleted>
                {subscriptions.map(({ id }) => (
                  <List.Item
                    key={`StripeSubscription__${id}`}
                    style={{ fontSize: 16, lineHeight: '155%' }}
                  >
                    <Link href={getStripeSubscriptionsUrl(id)} newPage>
                      {id}
                    </Link>
                  </List.Item>
                ))}
              </List>
            </>
          )}
          <br />
          <Text variant={TypographyStyles.H3}>
            Subscription Start Date:{' '}
            <GreenText>{earliestSubscriptionStartDate}</GreenText>
          </Text>
          <br />
          <LiteDowngradeEligibilityRow value={liteDowngradeEligibility} />
        </>
      )}
      {createdAt && (
        <>
          <br />
          <Text variant={TypographyStyles.H3}>
            User Account Created On:
            <GreenText>{` ${
              DateTime.fromISO(createdAt).toFormat(
                DATE_FORMATS_LUXON.DISPLAY_SHORT
              ) ?? ''
            }`}</GreenText>
          </Text>
        </>
      )}
    </Card>
  )
}
